/*
 * Copyright (c) 2024. World Wide Generation Ltd
 */

import React, { useMemo } from 'react';
import { Button } from 'reactstrap';
import { PILLAR_OPTIONS, SWOT_CATEGORY_OPTIONS, PILLAR_COLORS, SWOT_COLORS } from '../utils/constants';
import type { SectionType, SwotCategory, InsightSynthesis } from '@g17eco/types/insights-synthesis';
import { InsightsFilter } from '../hooks/useInsightsFilters';
import { getPillarIcon, getSwotIcon } from '../utils/helpers';

interface Props {
  filters: InsightsFilter;
  onFilterChange: (update: Partial<InsightsFilter>) => void;
  insights: InsightSynthesis[];
}

interface FilterChipProps {
  label: string;
  active: boolean;
  onClick: () => void;
  color?: string;
  icon?: string;
  count?: number;
}

const FilterChip = <T,>({ label, active, onClick, color, icon, count }: FilterChipProps) => (
  <Button
    color={active && color ? color : 'secondary'}
    outline={!active}
    onClick={onClick}
    className={`px-1 py-1 me-2 my-1 d-flex flex-column align-items-center ${!active ? 'text-muted' : ''}`}
    style={{
      minWidth: '5rem',
    }}
  >
    {icon && (
      <i className={`fas ${icon} mb-1 text-xl`} />
    )}
    <div className='d-flex align-items-center gap-1'>
      <span className='text-xs text-uppercase'>{label}</span>
      {count !== undefined && <span className='text-xs'>({count})</span>}
    </div>
  </Button>
);

export const InsightsFilters: React.FC<Props> = ({ filters, onFilterChange, insights }) => {
  // Calculate counts for each pillar
  const pillarCounts = useMemo(() => {
    const counts: Record<string, number> = {
      all: insights.length,
      environment: 0,
      economic: 0,
      social: 0,
      governance: 0,
    };

    insights.forEach((insight) => {
      const pillar = insight.source.metadata.sectionType;
      if (counts[pillar] !== undefined) {
        counts[pillar]++;
      }
    });

    return counts;
  }, [insights]);

  // Calculate counts for each SWOT category
  const swotCounts = useMemo(() => {
    const counts: Record<string, number> = {
      all: insights.length,
      strength: 0,
      weakness: 0,
      opportunity: 0,
      threat: 0,
    };

    insights.forEach((insight) => {
      const swot = insight.content.swotCategory;
      if (counts[swot] !== undefined) {
        counts[swot]++;
      }
    });

    return counts;
  }, [insights]);

  const handlePillarClick = (value: typeof filters.pillar) => {
    // Toggle: if clicking active filter, reset to 'all'
    if (filters.pillar === value) {
      onFilterChange({ pillar: 'all' });
    } else {
      onFilterChange({ pillar: value });
    }
  };

  const handleSwotClick = (value: typeof filters.swot) => {
    // Toggle: if clicking active filter, reset to 'all'
    if (filters.swot === value) {
      onFilterChange({ swot: 'all' });
    } else {
      onFilterChange({ swot: value });
    }
  };

  return (
    <div className='insights-filters row mb-2'>
      <div className='filter-section col-6'>
        <div className='mt-1 text-ThemeTextLight text-sm'>SWOT</div>
        <div className='d-flex flex-wrap'>
          {SWOT_CATEGORY_OPTIONS.map((option) => (
            <FilterChip
              key={option.value}
              label={option.label}
              active={filters.swot === option.value}
              onClick={() => handleSwotClick(option.value)}
              color={option.value !== 'all' ? SWOT_COLORS[option.value as SwotCategory] : undefined}
              icon={getSwotIcon(option.value)}
              count={swotCounts[option.value]}
            />
          ))}
        </div>
      </div>

      <div className='filter-section col-6 mb-3'>
        <div className='mt-1 text-ThemeTextLight text-sm'>PILLAR</div>
        <div className='d-flex flex-wrap'>
          {PILLAR_OPTIONS.map((option) => (
            <FilterChip
              key={option.value}
              label={option.label}
              active={filters.pillar === option.value}
              onClick={() => handlePillarClick(option.value)}
              color={option.value !== 'all' ? PILLAR_COLORS[option.value as SectionType] : undefined}
              icon={getPillarIcon(option.value)}
              count={pillarCounts[option.value]}
            />
          ))}
        </div>
      </div>

    </div>
  );
};
